import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { fetchInfluencers, fetchIGData, generateConnectLink, getUser } from '../api';
import { PLATFORMS, TIER_COLORS, CHORD, fmtK, fmtPct, fmtRate, S } from '../constants';

function StatCard({ label, value, color, sub }) {
  return (
    <div style={{
      background: CHORD.bgCard, border: `0.5px solid ${CHORD.border}`,
      borderRadius: 12, padding: '1rem 1.25rem', flex: 1, minWidth: 120,
    }}>
      <div style={{ fontSize: 11, color: CHORD.textMuted, marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.5px' }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 700, color: color || CHORD.beige }}>{value || '—'}</div>
      {sub && <div style={{ fontSize: 11, color: CHORD.textFaint, marginTop: 3 }}>{sub}</div>}
    </div>
  );
}

function InfoRow({ label, value, color }) {
  if (!value) return null;
  return (
    <div style={{ display: 'flex', gap: 12, padding: '8px 0', borderBottom: `0.5px solid ${CHORD.border}` }}>
      <div style={{ fontSize: 12, color: CHORD.textMuted, minWidth: 140 }}>{label}</div>
      <div style={{ fontSize: 13, color: color || CHORD.beige, fontWeight: 500, flex: 1 }}>{value}</div>
    </div>
  );
}

function Pills({ value, color }) {
  if (!value) return <span style={{ color: CHORD.textFaint }}>—</span>;
  const items = value.split(',').map(s => s.trim()).filter(Boolean);
  return (
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
      {items.map((item, i) => (
        <span key={i} style={{
          background: `${color}18`, border: `0.5px solid ${color}40`,
          color, borderRadius: 20, padding: '2px 10px', fontSize: 11, fontWeight: 500,
        }}>{item}</span>
      ))}
    </div>
  );
}

export default function InfluencerProfile() {
  const { platformKey, id } = useParams();
  const navigate  = useNavigate();
  const [inf, setInf]       = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError]   = useState('');
  const [syncing, setSyncing] = useState(false);
  const [syncMsg, setSyncMsg] = useState('');

  const platformInfo = PLATFORMS.find(p => p.key === platformKey);
  const user = getUser();

  useEffect(() => {
    async function load() {
      setLoading(true);
      try {
        const res = await fetchInfluencers(platformKey, {});
        const found = res.data.find(r => String(r.id) === String(id));
        if (!found) setError('Influencer not found');
        else setInf(found);
      } catch(e) { setError(e.message); }
      finally { setLoading(false); }
    }
    load();
  }, [platformKey, id]);

  async function handleSync() {
    setSyncing(true); setSyncMsg('');
    try {
      const data = await fetchIGData(platformKey, id);
      setSyncMsg(`✓ Synced! Followers: ${fmtK(data.followers)} · Eng: ${fmtPct(data.engagement_rate)}`);
      setInf(prev => ({...prev, ...data}));
    } catch(e) { setSyncMsg('✗ ' + e.message); }
    finally { setSyncing(false); }
  }

  async function handleCopyLink() {
    try {
      const data = await generateConnectLink(platformKey, id);
      await navigator.clipboard.writeText(data.connect_url);
      setSyncMsg('✓ Connect link copied to clipboard!');
    } catch(e) { setSyncMsg('✗ ' + e.message); }
  }

  if (loading) return (
    <div style={{ padding: '3rem', textAlign: 'center', color: CHORD.textMuted }}>Loading...</div>
  );
  if (error) return (
    <div style={{ padding: '3rem', textAlign: 'center', color: '#E55D4A' }}>{error}</div>
  );
  if (!inf) return null;

  const tierColor = TIER_COLORS[inf.tier] || CHORD.coral;
  const engColor  = inf.engagement_rate > 0.05 ? '#1D9E75' : CHORD.beige;

  return (
    <div style={{ maxWidth: 900, margin: '0 auto', padding: '2rem' }}>

      {/* Back button */}
      <button onClick={() => navigate(`/platform/${platformKey}`)} style={{
        background: 'none', border: 'none', color: CHORD.textMuted, cursor: 'pointer',
        fontSize: 13, fontFamily: 'inherit', marginBottom: '1.5rem', display: 'flex', alignItems: 'center', gap: 6,
      }}>← Back to {platformInfo?.label}</button>

      {/* Header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: '1.5rem', marginBottom: '2rem', flexWrap: 'wrap' }}>
        {/* Avatar placeholder */}
        <div style={{
          width: 72, height: 72, borderRadius: '50%', flexShrink: 0,
          background: `${platformInfo?.color}22`, border: `2px solid ${platformInfo?.color}`,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 28, fontWeight: 700, color: platformInfo?.color,
        }}>
          {inf.influencer_name?.charAt(0).toUpperCase()}
        </div>

        <div style={{ flex: 1 }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', marginBottom: 4 }}>
            <h1 style={{ fontSize: 22, fontWeight: 700, color: CHORD.beige, margin: 0 }}>{inf.influencer_name}</h1>
            <span style={{ background: `${tierColor}22`, color: tierColor, borderRadius: 20, padding: '3px 12px', fontSize: 11, fontWeight: 600 }}>{inf.tier}</span>
            {inf.brand_fit === 'Y' && <span style={{ background: 'rgba(29,158,117,0.15)', color: '#1D9E75', borderRadius: 20, padding: '3px 10px', fontSize: 11, fontWeight: 600 }}>Brand Fit ✓</span>}
          </div>

          {inf.handle && (
            <a href={inf.handle} target="_blank" rel="noreferrer" style={{ fontSize: 13, color: platformInfo?.color, textDecoration: 'none' }}>
              {inf.handle.replace('https://','').replace('www.','')}
            </a>
          )}

          <div style={{ fontSize: 12, color: CHORD.textMuted, marginTop: 4 }}>
            {[inf.city, inf.state, inf.country].filter(Boolean).join(', ')}
            {inf.language && ` · ${inf.language}`}
          </div>
        </div>

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
          {platformKey === 'ig' && (
            <>
              <button onClick={handleCopyLink} style={{
                background: 'rgba(225,48,108,0.12)', border: '0.5px solid rgba(225,48,108,0.3)',
                borderRadius: 8, padding: '8px 16px', fontSize: 12, color: '#E1306C',
                cursor: 'pointer', fontFamily: 'inherit',
              }}>🔗 Connect Link</button>
              {inf.ig_access_token && (
                <button onClick={handleSync} disabled={syncing} style={{
                  background: 'rgba(29,158,117,0.12)', border: '0.5px solid rgba(29,158,117,0.3)',
                  borderRadius: 8, padding: '8px 16px', fontSize: 12, color: '#1D9E75',
                  cursor: 'pointer', fontFamily: 'inherit', opacity: syncing ? 0.6 : 1,
                }}>{syncing ? '⏳ Syncing...' : '↻ Sync Live Data'}</button>
              )}
            </>
          )}
          <button onClick={() => navigate(`/platform/${platformKey}`, { state: { editId: inf.id } })} style={{
            ...S.btnPrimary, fontSize: 12, padding: '8px 16px',
          }}>✏ Edit Profile</button>
        </div>
      </div>

      {syncMsg && (
        <div style={{ marginBottom: '1rem', fontSize: 13, color: syncMsg.startsWith('✓') ? '#1D9E75' : '#E55D4A',
          padding: '8px 14px', background: syncMsg.startsWith('✓') ? 'rgba(29,158,117,0.1)' : 'rgba(229,93,74,0.1)',
          borderRadius: 8, border: `0.5px solid ${syncMsg.startsWith('✓') ? 'rgba(29,158,117,0.3)' : 'rgba(229,93,74,0.3)'}`,
        }}>{syncMsg}</div>
      )}

      {/* Metrics */}
      <div style={{ display: 'flex', gap: 10, marginBottom: '2rem', flexWrap: 'wrap' }}>
        <StatCard label="Followers"      value={fmtK(inf.followers)} color={CHORD.beige} />
        <StatCard label="Avg Views/Post" value={fmtK(inf.avg_views)} />
        <StatCard label="Engagement Rate" value={fmtPct(inf.engagement_rate)} color={engColor} />
        <StatCard label="Avg Reach/Post" value={fmtK(inf.reach)} />
        <StatCard label="Posts (30d)"    value={inf.post_count_30d} sub="last 30 days" />
        <StatCard label="Videos (30d)"   value={inf.video_count_30d} sub="last 30 days" />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem' }}>

        {/* Basic Info */}
        <div style={{ background: CHORD.bgCard, border: `0.5px solid ${CHORD.border}`, borderRadius: 14, padding: '1.25rem' }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: CHORD.textFaint, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '1rem' }}>Profile</div>
          <InfoRow label="Niche / Category" value={inf.niche} />
          <InfoRow label="Audience" value={inf.audience} />
          <InfoRow label="Language(s)" value={inf.language} />
          <InfoRow label="Relationship" value={inf.relationship_status} color={
            inf.relationship_status?.toLowerCase() === 'active' ? '#1D9E75' :
            inf.relationship_status?.toLowerCase() === 'inactive' ? '#E55D4A' : CHORD.orange
          } />
          <InfoRow label="Brand Fit" value={inf.brand_fit === 'Y' ? 'Yes ✓' : inf.brand_fit === 'N' ? 'No' : null}
            color={inf.brand_fit === 'Y' ? '#1D9E75' : '#E55D4A'} />
        </div>

        {/* Rates */}
        <div style={{ background: CHORD.bgCard, border: `0.5px solid ${CHORD.border}`, borderRadius: 14, padding: '1.25rem' }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: CHORD.textFaint, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '1rem' }}>Rates</div>
          <InfoRow label="Base Rate" value={fmtRate(inf.rate)} color={CHORD.orange} />
          <InfoRow label="Stories" value={fmtRate(inf.rate_stories)} color={CHORD.orange} />
          <InfoRow label="Reels" value={fmtRate(inf.rate_reels)} color={CHORD.orange} />
          <InfoRow label="Static Post" value={fmtRate(inf.rate_static)} color={CHORD.orange} />
          <InfoRow label="Shoot" value={fmtRate(inf.rate_shoot)} color={CHORD.orange} />
          <InfoRow label="Affiliate" value={fmtRate(inf.rate_affiliate)} color={CHORD.orange} />
          {inf.preferred_deliverables && (
            <div style={{ paddingTop: 10 }}>
              <div style={{ fontSize: 12, color: CHORD.textMuted, marginBottom: 6 }}>Preferred Deliverables</div>
              <Pills value={inf.preferred_deliverables} color={CHORD.lime} />
            </div>
          )}
        </div>

        {/* Collaboration */}
        <div style={{ background: CHORD.bgCard, border: `0.5px solid ${CHORD.border}`, borderRadius: 14, padding: '1.25rem' }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: CHORD.textFaint, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '1rem' }}>Collaboration</div>
          <InfoRow label="Email / Contact" value={inf.email_contact} />
          <InfoRow label="Agency / Manager" value={inf.agency_manager} />
          {inf.previous_collab && (
            <div style={{ padding: '8px 0', borderBottom: `0.5px solid ${CHORD.border}` }}>
              <div style={{ fontSize: 12, color: CHORD.textMuted, marginBottom: 6 }}>Past Collaborations</div>
              <Pills value={inf.previous_collab} color={CHORD.orange} />
            </div>
          )}
          {inf.brands && (
            <div style={{ padding: '8px 0', borderBottom: `0.5px solid ${CHORD.border}` }}>
              <div style={{ fontSize: 12, color: CHORD.textMuted, marginBottom: 6 }}>Brands</div>
              <Pills value={inf.brands} color="#2C7CE5" />
            </div>
          )}
          {inf.past_work && <InfoRow label="Past Work" value={inf.past_work} />}
        </div>

        {/* Notes */}
        <div style={{ background: CHORD.bgCard, border: `0.5px solid ${CHORD.border}`, borderRadius: 14, padding: '1.25rem' }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: CHORD.textFaint, textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '1rem' }}>Notes</div>
          {inf.notes
            ? <p style={{ fontSize: 13, color: CHORD.beige, lineHeight: 1.6 }}>{inf.notes}</p>
            : <p style={{ fontSize: 13, color: CHORD.textFaint }}>No notes added yet.</p>
          }
          <div style={{ marginTop: '1rem', paddingTop: '1rem', borderTop: `0.5px solid ${CHORD.border}` }}>
            <div style={{ fontSize: 11, color: CHORD.textFaint }}>
              Added: {inf.created_at ? new Date(inf.created_at).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' }) : '—'}
            </div>
            {inf.updated_at && (
              <div style={{ fontSize: 11, color: CHORD.textFaint, marginTop: 2 }}>
                Last updated: {new Date(inf.updated_at).toLocaleDateString('en-IN', { day:'numeric', month:'short', year:'numeric' })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
